// WARNING: ExperimentBadge.tsx imports isPortalContext() directly — NOT
// shouldShowDevControls(). The badge must only render inside a same-origin
// parent (staff portal iframe); shouldShowDevControls() also returns true
// for direct URLs in prod, which is wrong for the badge. Do NOT migrate
// ExperimentBadge to shouldShowDevControls "for consistency."
//
// TRAC-486 + TRAC-604: "Portal context" = this embed is rendered inside a
// same-origin parent page (i.e. the staff cognito portal embedding /embed).
// Browser security: reading window.top.location.origin throws SecurityError
// when the parent frame is cross-origin, so only same-origin parents get
// through. /embed is served from *.traci.mi.tirerack.com, so any same-origin
// parent necessarily IS the portal.
//
// `_seam` is a mockable indirection layer: tests do
// `vi.spyOn(_seam, 'getTop').mockReturnValue(...)` to inject fake top frames
// without redefining the non-configurable `window.top` property. The seam is
// an object (not bare exports) because vi.spyOn on a bare ESM export does not
// affect in-module callers — they'd resolve `_getTop` against the original
// local binding, bypassing the spy. Property access on a shared object does
// route through the spy. Production callers ignore it; the underscore prefix
// signals "internal — for tests."

export const _seam = {
  getTop: (): Window | null => window.top,
  getWindow: (): Window | undefined =>
    typeof window === 'undefined' ? undefined : window,
};

export function isPortalContext(): boolean {
  try {
    const top = _seam.getTop();
    const win = _seam.getWindow();
    return top !== null && top !== win && !!top.location.origin;
  } catch {
    return false;
  }
}

// TRAC-604 test-harness escape hatch: `?force_gate_env=prod` lets a non-prod
// build exercise the prod gate code path (so the cross-origin iframe in
// /embed-gating-test's download flow can demonstrate Cell 4 — controls hidden
// — without waiting for a prod cutover). The override is strictly one-way:
//   - Real prod always behaves as prod regardless of the param.
//   - In non-prod, the param can only push toward `'prod'`, never weaken.
// Worst-case footgun is a non-prod URL that hides Dev Settings; no security
// surface because the override cannot reveal anything.
function getEffectiveGateEnv(env: string): string {
  if (env === 'prod') return 'prod';
  try {
    const win = _seam.getWindow();
    if (!win) return env;
    const forced = new URLSearchParams(win.location.search).get('force_gate_env');
    return forced === 'prod' ? 'prod' : env;
  } catch {
    return env;
  }
}

// TRAC-604: Hide internal/dev controls (Clear conversation, TurnFeedback
// copy-trace, the turn bridge) only when this embed is rendered inside
// a cross-origin customer page in prod. Everywhere else (local, dev, qa,
// direct URL, same-origin staff portal iframe) controls are visible. The Dev
// Settings gear and drawer are NOT on this gate: see shouldShowDevSettings
// below (TRACI-933), which hides them on every prod build.
//
// Trace of the four prod cases (only env where the rule applies):
//   - direct URL (no parent frame):     window.top === window         → visible
//   - same-origin parent (staff portal): isPortalContext() === true    → visible
//   - cross-origin parent (customer):    isPortalContext() === false   → hidden
//   - no window (SSR):                   typeof window === 'undefined' → visible (no-op)
export function shouldShowDevControls(env: string): boolean {
  const effective = getEffectiveGateEnv(env);
  if (effective !== 'prod') return true;
  const win = _seam.getWindow();
  if (!win) return true;
  try {
    if (_seam.getTop() === win) return true;
  } catch {
    return false;
  }
  return isPortalContext();
}

// TRACI-933: the Dev Settings gear and its drawer never render on a prod
// build, in any frame and for any role (direct URL and the staff portal
// included). Every lever in the drawer (agent version and model, live chat
// hours, handoff, experiment overrides, region pin, chaos drill, chunk timeout,
// break the stream, live chat bridge) is a dev/QA test switch. Clear
// conversation, TurnFeedback and the turn bridge keep shouldShowDevControls.
// `?force_gate_env=prod` hides it on a non-prod build too, same one-way rule.
export function shouldShowDevSettings(env: string): boolean {
  return getEffectiveGateEnv(env) !== 'prod';
}
